import { formatMoney } from '@theme/money-formatting';

/**
 * recovr plan selector
 *
 * Bridges the styled plan cards (real radio inputs) to Horizon's <variant-picker>
 * and to the section's add-to-cart form(s). Each card carries a variant id plus an
 * optional selling plan id: selecting a card checks the matching variant-picker
 * input (price, buy buttons, media, URL and sticky add-to-cart all update through
 * Horizon's own variant flow) and sets/removes a hidden `selling_plan` field so
 * subscriptions ride the native product form. Without JS the form degrades to a
 * one-time purchase of the picker's variant.
 */
class RecovrPlanSelector extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#onPlanChange, { signal });

    // Keep the cards in sync when the variant changes elsewhere (hidden picker,
    // URL parameter restore, section morphs).
    document.addEventListener('variant:update', this.#onVariantUpdate, { signal });

    // Re-price everything when a quantity field changes anywhere in the
    // section (the buy box has none today, but if one is added the top price
    // and button keep multiplying correctly with zero extra wiring).
    this.#scope.addEventListener?.(
      'input',
      (event) => {
        const t = event.target;
        if (t instanceof HTMLInputElement && t.name === 'quantity') this.#applyInput(this.#checkedInput);
      },
      { signal }
    );

    // Late-loading scripts (the Seal subscriptions embed) render their own
    // empty `selling_plan` field and can wipe the value we set on boot,
    // silently turning the pre-selected subscription into a one-time add.
    // Re-assert once everything has loaded, and — bulletproof — re-assert
    // synchronously in the capture phase of every submit so the posted form
    // always matches the checked card.
    window.addEventListener('load', () => this.#applyInput(this.#checkedInput), { signal });
    this.#scope.addEventListener?.(
      'submit',
      () => {
        const input = this.#checkedInput;
        if (!input) return;
        this.#writeFormVariantId(input.dataset.variantId ?? '');
        this.#setSellingPlan(input.dataset.sellingPlanId ?? '');
      },
      { capture: true, signal }
    );

    // Apply the server-rendered checked card so the initial add-to-cart carries
    // the right variant AND selling plan (the default card is a subscription).
    // Re-apply once the DOM has fully parsed too: the custom element can upgrade
    // before the buy box's own <form> exists, in which case the first pass finds
    // no form to write the variant id onto.
    this.#applyInput(this.#checkedInput);
    requestAnimationFrame(() => this.#applyInput(this.#checkedInput));
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#applyInput(this.#checkedInput), {
        once: true,
        signal,
      });
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  get #checkedInput() {
    const input = this.querySelector('input[data-variant-id]:checked');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * @param {Event} event
   */
  #onPlanChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.variantId || !input.checked) return;
    this.#applyInput(input);
  };

  /**
   * Applies a card selection: variant picker sync, selling plan field, price label.
   * @param {HTMLInputElement | null} input
   */
  #applyInput(input) {
    if (!input) return;
    this.#selectVariant(input.dataset.variantId ?? '');
    this.#setSellingPlan(input.dataset.sellingPlanId ?? '');
    this.#updateAllPrices(input);
    this.#dispatchPlanChange(input);
  }

  /**
   * Announces the selected plan on the section so decoupled blocks (the
   * buy buttons, a future app widget) can react without any DOM-order or
   * parent/child assumptions. The direct form writes above remain the proven
   * path; this event is additive.
   * @param {HTMLInputElement} input
   */
  #dispatchPlanChange(input) {
    const scope = this.#scope;
    const target = scope instanceof Document ? document.documentElement : scope;
    target.dispatchEvent(
      new CustomEvent('recovr:plan:change', {
        bubbles: true,
        detail: {
          productId: this.dataset.productId ?? '',
          variantId: input.dataset.variantId ?? '',
          sellingPlanId: input.dataset.sellingPlanId ?? '',
          priceCents: parseInt(input.dataset.priceCents ?? '', 10) || 0,
          priceFormatted: input.dataset.priceFormatted ?? '',
        },
      })
    );
  }

  /**
   * Current quantity from any quantity field in the section (1 when absent).
   * @returns {number}
   */
  get #quantity() {
    const field = this.#scope.querySelector?.('input[name="quantity"]');
    const qty = field instanceof HTMLInputElement ? parseInt(field.value, 10) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  /**
   * Formats minor units with the theme's own money module, using the shop's
   * configured money format + currency captured server-side — decimals and
   * separators match the Liquid `money` filter for every currency.
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  /**
   * Syncs every price surface to the selected card × quantity: the ATC button
   * label, the buy box's top price + strikethrough, and the save chip.
   * @param {HTMLInputElement} input
   */
  #updateAllPrices(input) {
    const qty = this.#quantity;
    const priceCents = parseInt(input.dataset.priceCents ?? '', 10);
    const wasCents = parseInt(input.dataset.wasCents ?? '', 10) || 0;

    // ATC button label (and any other opt-in label).
    const label =
      Number.isFinite(priceCents) && qty > 1
        ? this.#money(priceCents * qty)
        : (input.dataset.priceFormatted ?? '');
    this.#updatePriceLabels(label);

    if (!Number.isFinite(priceCents)) return;
    const scope = this.#scope;

    // Top price: current amount + strikethrough of the selected card's anchor.
    const now = scope.querySelector?.('.recovr-buybox__price-now');
    if (now) {
      now.textContent = this.#money(priceCents * qty);
      this.#bump(now);
    }
    const priceWrap = scope.querySelector?.('.recovr-buybox__price');
    let was = scope.querySelector?.('.recovr-buybox__price-was');
    if (wasCents > priceCents) {
      if (!was && priceWrap) {
        was = document.createElement('s');
        was.className = 'recovr-buybox__price-was';
        priceWrap.appendChild(was);
      }
      if (was) {
        was.textContent = this.#money(wasCents * qty);
        was.style.display = '';
      }
    } else if (was) {
      was.style.display = 'none';
    }

    // "Save $X" chip next to the rating mirrors the selected card's saving.
    // Its wording comes from the buy box's customizer setting, carried on the
    // element as data-template ("[amount]" is replaced with the live saving).
    const chip = scope.querySelector?.('.recovr-buybox__save-chip');
    if (chip) {
      if (wasCents > priceCents) {
        const template = chip.dataset.template || 'Save [amount]';
        chip.textContent = template.replace('[amount]', this.#money((wasCents - priceCents) * qty));
        chip.style.display = '';
      } else {
        chip.style.display = 'none';
      }
    }
  }

  /**
   * Soft scale "bump" on a price element so the update is noticeable.
   * Skipped for reduced-motion users.
   * @param {Element} el
   */
  #bump(el) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.classList.remove('recovr-price-bump');
    // restart the animation on consecutive updates
    void /** @type {HTMLElement} */ (el).offsetWidth;
    el.classList.add('recovr-price-bump');
  }

  /**
   * @returns {Element | Document}
   */
  get #scope() {
    return this.closest('.shopify-section') || document;
  }

  /**
   * @returns {Element | null}
   */
  get #variantPicker() {
    return this.#scope.querySelector('variant-picker');
  }

  /**
   * Applies the chosen variant. When Horizon's <variant-picker> is present it
   * drives that (price/media/URL update natively). When it is NOT present — e.g.
   * the recovr buy box renders its own product form with a plain hidden id field —
   * we write the id straight onto the form so the add-to-cart posts the right
   * variant. Both paths run so a stale hidden id can never be left behind.
   * @param {string} variantId
   */
  #selectVariant(variantId) {
    if (variantId) this.#writeFormVariantId(variantId);

    const picker = this.#variantPicker;
    if (!picker) return;

    const target = picker.querySelector(
      `input[data-variant-id="${variantId}"], option[data-variant-id="${variantId}"]`
    );
    if (!target) return;

    if (target instanceof HTMLInputElement) {
      if (target.checked) return;
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (target instanceof HTMLOptionElement) {
      const select = target.closest('select');
      if (!select || select.value === target.value) return;
      select.value = target.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Writes the variant id onto every add-to-cart form's hidden `id` field in
   * scope (the buy box's own form has no variant-picker to drive it).
   * @param {string} variantId
   */
  #writeFormVariantId(variantId) {
    this.#scope.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
      const field = form.querySelector('input[name="id"]');
      if (field && field.value !== variantId) field.value = variantId;
    });
  }

  /**
   * Sets (or removes) the hidden `selling_plan` field on every add-to-cart form
   * in this section, so both the main form and the homepage buy box form post
   * the subscription with the native Cart AJAX flow.
   * @param {string} sellingPlanId - numeric part or full gid; '' clears the plan
   */
  #setSellingPlan(sellingPlanId) {
    const planId = sellingPlanId.split('/').pop() ?? '';
    this.#scope.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
      let field = form.querySelector('input[name="selling_plan"]');
      if (planId) {
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'selling_plan';
          form.appendChild(field);
        }
        field.value = planId;
      } else {
        field?.remove();
      }
    });
  }

  /**
   * Mirrors the selected card's effective price into any opt-in labels
   * (e.g. the homepage buy box ATC button price).
   * @param {string} priceFormatted
   */
  #updatePriceLabels(priceFormatted) {
    if (!priceFormatted) return;
    this.#scope.querySelectorAll('[data-recovr-atc-price]').forEach((el) => {
      el.textContent = priceFormatted;
    });
  }

  /**
   * @param {Event & { detail?: { resource?: { id?: number | string }, data?: { productId?: string } } }} event
   */
  #onVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    const productId = event.detail?.data?.productId;
    if (variantId == null) return;
    if (productId && this.dataset.productId && productId !== this.dataset.productId) return;

    const current = this.#checkedInput;
    if (current?.dataset.variantId === String(variantId)) return;

    const input = this.querySelector(`input[data-variant-id="${variantId}"]`);
    if (input instanceof HTMLInputElement && !input.checked) {
      input.checked = true;
      // External variant change lands on that variant's first card; keep the
      // selling plan field and every price surface consistent with the new card.
      this.#setSellingPlan(input.dataset.sellingPlanId ?? '');
      this.#updateAllPrices(input);
      this.#dispatchPlanChange(input);
    }
  };
}

if (!customElements.get('recovr-plan-selector')) {
  customElements.define('recovr-plan-selector', RecovrPlanSelector);
}
